import React, { useEffect, useRef, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, Stack, MenuItem, CircularProgress, Typography, Box,
} from '@mui/material';
import { InfoCircle } from 'iconsax-react';
import { useTranslation } from 'react-i18next';
import { Task, TaskApiStatus, WorkStatus } from '../../types/task.type';
import { WORK_STATUSES, buildWorkStatusWrite, getWorkStatus, workStatusLabelKey } from '../../utils/task.utils';
import {
  dateInputToRfc3339,
  getApiErrorDetail,
  rfc3339ToDateInput,
} from '../../utils/taskApi.utils';
import { useToast, TOAST_TYPES } from '../../providers/ToastProvider';
import TaskThreadMessages from './TaskThreadMessages';

export type TaskFormMode = 'employee' | 'my';

interface EmployeeOption {
  id: string;
  name: string;
}

interface TaskFormDialogProps {
  open: boolean;
  mode: TaskFormMode;
  task?: Task | null;
  employeeOptions?: EmployeeOption[];
  defaultEmployeeId?: string;
  saving?: boolean;
  onClose: () => void;
  onSubmitEmployee: (payload: {
    employee_id: string;
    title: string;
    notes?: string;
    due?: string;
    status?: TaskApiStatus;
    work_status?: WorkStatus;
  }) => Promise<void>;
  onSubmitMy: (payload: {
    title: string;
    notes?: string;
    due?: string;
    status?: TaskApiStatus;
    work_status?: WorkStatus;
  }) => Promise<void>;
  onSubmitEditEmployee: (payload: {
    employee_id: string;
    task_list_id: string;
    title?: string;
    notes?: string;
    due?: string | null;
    status?: TaskApiStatus;
    work_status?: WorkStatus;
  }) => Promise<void>;
  onSubmitEditMy: (payload: {
    task_list_id: string;
    title?: string;
    notes?: string;
    due?: string | null;
    status?: TaskApiStatus;
    work_status?: WorkStatus;
  }) => Promise<void>;
}

export default function TaskFormDialog({
  open,
  mode,
  task,
  employeeOptions = [],
  defaultEmployeeId = '',
  saving = false,
  onClose,
  onSubmitEmployee,
  onSubmitMy,
  onSubmitEditEmployee,
  onSubmitEditMy,
}: TaskFormDialogProps) {
  const { t } = useTranslation();
  const { showToast } = useToast();
  const isEdit = !!task;
  const wasOpen = useRef(false);

  const [employeeId, setEmployeeId] = useState('');
  const [title, setTitle] = useState('');
  const [notes, setNotes] = useState('');
  const [due, setDue] = useState('');
  const [workStatus, setWorkStatus] = useState<WorkStatus>('not_started');
  const [submitting, setSubmitting] = useState(false);

  const busy = saving || submitting;

  useEffect(() => {
    const justOpened = open && !wasOpen.current;
    wasOpen.current = open;

    if (!justOpened) return;

    setEmployeeId(task?.employee_id || defaultEmployeeId || employeeOptions[0]?.id || '');
    setTitle(task?.title ?? '');
    setNotes(task?.notes ?? '');
    setDue(rfc3339ToDateInput(task?.due));
    setWorkStatus(task ? getWorkStatus(task) : 'not_started');
    setSubmitting(false);
  }, [open, task?.google_task_id, defaultEmployeeId]);

  const handleSubmit = async () => {
    if (!title.trim()) {
      showToast(t('tasks.titleRequired'), TOAST_TYPES.WARNING);
      return;
    }

    setSubmitting(true);
    try {
      const dueIso = dateInputToRfc3339(due);

      if (isEdit && task) {
        const editPayload = {
          title: title.trim(),
          notes: notes.trim() || undefined,
          due: dueIso ?? null,
          ...buildWorkStatusWrite(task, workStatus),
        };
        if (mode === 'my') {
          await onSubmitEditMy({ task_list_id: task.task_list_id, ...editPayload });
        } else {
          await onSubmitEditEmployee({
            employee_id: task.employee_id,
            task_list_id: task.task_list_id,
            ...editPayload,
          });
        }
      } else if (mode === 'my') {
        await onSubmitMy({
          title: title.trim(),
          notes: notes.trim() || undefined,
          due: dueIso,
        });
      } else {
        if (!employeeId) {
          showToast(t('tasks.employeeRequired'), TOAST_TYPES.WARNING);
          return;
        }
        await onSubmitEmployee({
          employee_id: employeeId,
          title: title.trim(),
          notes: notes.trim() || undefined,
          due: dueIso,
        });
      }
      onClose();
    } catch (e) {
      showToast(getApiErrorDetail(e, t('common.error')), TOAST_TYPES.ERROR);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
      <DialogTitle sx={{ fontWeight: 700 }}>
        {isEdit ? t('tasks.editTask') : t('tasks.addTask')}
      </DialogTitle>
      <DialogContent sx={{ pt: 1 }}>
        <Stack spacing={2.5} sx={{ mt: 1 }}>
          {!isEdit && (
            <Box sx={{
              display: 'flex', gap: 1.25, alignItems: 'flex-start',
              px: 1.5, py: 1.25, borderRadius: 2,
              bgcolor: '#F0F9FF', border: '1px solid #BFDBFE',
            }}>
              <InfoCircle size={18} color="#2563EB" style={{ flexShrink: 0, marginTop: 2 }} />
              <Typography sx={{ fontSize: '0.8125rem', color: '#1E40AF', lineHeight: 1.55 }}>
                {mode === 'employee' ? t('tasks.createPersonalNoteEmployee') : t('tasks.createPersonalNoteMy')}
              </Typography>
            </Box>
          )}

          {mode === 'employee' && !isEdit && (
            <Box>
              <Typography sx={{ fontSize: '0.875rem', fontWeight: 500, mb: 0.75, color: '#374151' }}>
                {t('tasks.employee')} <span style={{ color: '#EF4444' }}>*</span>
              </Typography>
              <TextField
                select
                size="small"
                fullWidth
                value={employeeId}
                onChange={e => setEmployeeId(e.target.value)}
                disabled={busy}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    '& fieldset': { borderColor: '#E5E7EB' },
                    '&:hover fieldset': { borderColor: '#D1D5DB' },
                    '&.Mui-focused fieldset': { borderColor: '#3B82F6' },
                  },
                }}
              >
                {employeeOptions.map(emp => (
                  <MenuItem key={emp.id} value={emp.id}>{emp.name}</MenuItem>
                ))}
              </TextField>
            </Box>
          )}

          {isEdit && task && (
            <Typography sx={{ fontSize: '0.8125rem', color: 'text.secondary' }}>
              {t('tasks.space')}: {task.space_name ?? t('tasks.personalTasks')}
              {mode === 'employee' && ` · ${task.employee_name}`}
            </Typography>
          )}

          <Box>
            <Typography sx={{ fontSize: '0.875rem', fontWeight: 500, mb: 0.75, color: '#374151' }}>
              {t('tasks.taskTitle')} <span style={{ color: '#EF4444' }}>*</span>
            </Typography>
            <TextField
              size="small"
              fullWidth
              value={title}
              onChange={e => setTitle(e.target.value)}
              disabled={busy}
              placeholder={t('tasks.taskTitle')}
              sx={{
                '& .MuiOutlinedInput-root': {
                  '& fieldset': { borderColor: '#E5E7EB' },
                  '&:hover fieldset': { borderColor: '#D1D5DB' },
                  '&.Mui-focused fieldset': { borderColor: '#3B82F6' },
                },
              }}
            />
          </Box>

          <Box>
            <Typography sx={{ fontSize: '0.875rem', fontWeight: 500, mb: 0.75, color: '#374151' }}>
              {t('tasks.notes')}
            </Typography>
            <TextField
              size="small"
              fullWidth
              multiline
              rows={3}
              value={notes}
              onChange={e => setNotes(e.target.value)}
              disabled={busy}
              placeholder={t('tasks.notes')}
              sx={{
                '& .MuiOutlinedInput-root': {
                  '& fieldset': { borderColor: '#E5E7EB' },
                  '&:hover fieldset': { borderColor: '#D1D5DB' },
                  '&.Mui-focused fieldset': { borderColor: '#3B82F6' },
                },
              }}
            />
          </Box>

          <Box>
            <Typography sx={{ fontSize: '0.875rem', fontWeight: 500, mb: 0.75, color: '#374151' }}>
              {t('tasks.dueDate')}
            </Typography>
            <TextField
              type="date"
              size="small"
              fullWidth
              value={due}
              onChange={e => setDue(e.target.value)}
              disabled={busy}
              sx={{
                '& .MuiOutlinedInput-root': {
                  '& fieldset': { borderColor: '#E5E7EB' },
                  '&:hover fieldset': { borderColor: '#D1D5DB' },
                  '&.Mui-focused fieldset': { borderColor: '#3B82F6' },
                },
              }}
            />
          </Box>

          {isEdit && (
          <Box>
            <Typography sx={{ fontSize: '0.875rem', fontWeight: 500, mb: 0.75, color: '#374151' }}>
              {t('tasks.tableStatus')}
            </Typography>
            <TextField
              select
              size="small"
              fullWidth
              value={workStatus}
              onChange={e => setWorkStatus(e.target.value as WorkStatus)}
              disabled={busy}
              sx={{
                '& .MuiOutlinedInput-root': {
                  '& fieldset': { borderColor: '#E5E7EB' },
                  '&:hover fieldset': { borderColor: '#D1D5DB' },
                  '&.Mui-focused fieldset': { borderColor: '#3B82F6' },
                },
              }}
            >
              {WORK_STATUSES.map(value => (
                <MenuItem key={value} value={value}>{t(workStatusLabelKey(value))}</MenuItem>
              ))}
            </TextField>
          </Box>
          )}

          {isEdit && task && (
            <TaskThreadMessages
              threadLink={task.thread_link}
              threadMessages={task.thread_messages}
            />
          )}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={onClose} disabled={busy} sx={{ color: 'text.secondary' }}>
          {t('common.cancel')}
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={busy || !title.trim()}
          startIcon={busy ? <CircularProgress size={14} color="inherit" /> : undefined}
        >
          {isEdit ? t('common.save') : t('tasks.addTask')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
